import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { FileText, Plus, X } from 'lucide-react';
import { DatabaseSchema, UserPreferences } from '../../types';

interface Props {
  database: DatabaseSchema | null;
  onUpdatePreferences: (prefs: UserPreferences) => Promise<void>;
}

type ListKey = 'conditions' | 'familyHistory' | 'allergies';

const LIST_LABELS: Record<ListKey, string> = {
  conditions: 'Conditions & history',
  familyHistory: 'Family history',
  allergies: 'Allergies',
};

export const MedicalHistory: React.FC<Props> = ({ database, onUpdatePreferences }) => {
  const prefs = database?.preferences;
  const ctx = prefs?.medicalContext ?? {};
  const [drafts, setDrafts] = useState<Record<ListKey, string>>({ conditions: '', familyHistory: '', allergies: '' });

  if (!prefs) return null;

  const add = async (key: ListKey) => {
    const value = drafts[key].trim();
    if (!value) return;
    const current = ctx[key] ?? [];
    await onUpdatePreferences({
      ...prefs,
      medicalContext: { ...ctx, [key]: [...current, value] },
    });
    setDrafts((d) => ({ ...d, [key]: '' }));
  };

  const remove = async (key: ListKey, item: string) => {
    const current = ctx[key] ?? [];
    await onUpdatePreferences({
      ...prefs,
      medicalContext: { ...ctx, [key]: current.filter((c) => c !== item) },
    });
  };

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
            <FileText size={22} />
            <Typography variant="h6" fontWeight={700}>Medical history</Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Context your GP asked about — surfaces nowhere except here.
          </Typography>
        </CardContent>
      </Card>

      {(Object.keys(LIST_LABELS) as ListKey[]).map((key) => {
        const items = ctx[key] ?? [];
        return (
          <Card key={key} sx={{ mb: 1.5 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>{LIST_LABELS[key]}</Typography>
              {items.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>Nothing recorded yet.</Typography>
              )}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
                {items.map((item) => (
                  <Chip
                    key={item}
                    label={item}
                    onDelete={() => remove(key, item)}
                    deleteIcon={<X size={14} />}
                  />
                ))}
              </Box>
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  placeholder="Add item"
                  value={drafts[key]}
                  onChange={(e) => setDrafts((d) => ({ ...d, [key]: e.target.value }))}
                  onKeyDown={(e) => e.key === 'Enter' && add(key)}
                  fullWidth
                />
                <IconButton color="primary" onClick={() => add(key)}>
                  <Plus size={18} />
                </IconButton>
              </Stack>
            </CardContent>
          </Card>
        );
      })}

      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Notes</Typography>
          <TextField
            multiline
            rows={4}
            size="small"
            fullWidth
            placeholder="Free-text notes (meds, recent tests, anything else)"
            value={ctx.notes ?? ''}
            onChange={(e) => onUpdatePreferences({ ...prefs, medicalContext: { ...ctx, notes: e.target.value } })}
          />
          <Button
            variant="text"
            size="small"
            sx={{ mt: 1 }}
            onClick={() => onUpdatePreferences({ ...prefs, medicalContext: { ...ctx } })}
          >
            Save
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};
